import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, ArrowRight } from "lucide-react";
import { useLang } from "../LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActualiteDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/news/${id}`);
        if (!cancelled) setItem(data);
      } catch {
        if (!cancelled) setError("not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div className="container-page py-24" data-testid="actualite-not-found">
        <p className="eyebrow mb-3">{t("actualites.not_found_eyebrow")}</p>
        <h1 className="font-heading text-3xl text-ink mb-6">
          {t("actualites.not_found_title")}
        </h1>
        <Link to="/actualites" className="btn-outline">
          <ArrowLeft size={16} /> {t("actualites.back")}
        </Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container-page py-24 animate-pulse" data-testid="actualite-loading">
        <div className="h-4 w-40 bg-cream-200 rounded mb-4" />
        <div className="h-10 w-2/3 bg-cream-200 rounded mb-3" />
        <div className="h-72 w-full bg-cream-200 rounded-3xl mt-8" />
      </div>
    );
  }

  return (
    <div data-testid="page-actualite-detail">
      <section className="container-page pt-12 sm:pt-16 pb-6">
        <Link
          to="/actualites"
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest mb-8"
          data-testid="actualite-back"
        >
          <ArrowLeft size={15} /> {t("actualites.back")}
        </Link>

        <p className="eyebrow mb-4">{t("actualites.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium text-ink max-w-3xl leading-tight">
          {item.title}
        </h1>
        <div className="mt-5 flex items-center gap-2 text-sm text-ink-muted">
          <Calendar size={14} />
          <time>
            {new Date(item.published_at).toLocaleDateString(
              lang === "fr" ? "fr-FR" : "en-GB",
              { day: "numeric", month: "long", year: "numeric" }
            )}
          </time>
        </div>
      </section>

      {item.image_url && (
        <section className="container-page pb-8">
          <div className="rounded-[2rem] overflow-hidden shadow-lift">
            <img
              src={item.image_url}
              alt={item.title}
              className="w-full max-h-[520px] object-cover"
            />
          </div>
        </section>
      )}

      <section className="container-page pb-16">
        <div className="max-w-3xl">
          <p className="font-heading text-xl text-ink leading-relaxed mb-6">{item.excerpt}</p>
          <div className="text-ink-muted leading-relaxed whitespace-pre-wrap">{item.content}</div>
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="rounded-[2.5rem] bg-forest-light/60 border border-forest-light px-8 sm:px-12 py-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <h3 className="font-heading text-2xl font-semibold text-ink">
              {t("actualites.cta_title")}
            </h3>
            <p className="text-ink-muted mt-2 max-w-lg">{t("actualites.cta_text")}</p>
          </div>
          <Link to="/contact" className="btn-primary shrink-0" data-testid="actualite-cta-contact">
            {t("actualites.cta_button")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
